import { Button, Card, CardContent, CardHeader, CardTitle } from '@repo/shadcn';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';

import { ConfirmButton } from '@/components/ui/button';

import { useUpdateWholesaleRequestMutation } from '../../../features/slice/wholesale-admin';
import { isEqualObject } from '../../../utils/compare-object.util';
import { useWholesaleOrderRequestDetailsContext } from './context';
import { ConfirmOrderForm } from './schema';

export const ActionButton = () => {
    const {
        handleRejectWholesaleRequest,
        handleSubmit,
        handleConfirmAndPrint,
        isConfirming,
        isRejecting,
        data,
    } = useWholesaleOrderRequestDetailsContext();
    const { getValues } = useFormContext<ConfirmOrderForm>();

    const [updateWholesaleRequest, { isLoading: isUpdating }] =
        useUpdateWholesaleRequestMutation();

    const handleSave = async () => {
        const isEqual = isEqualObject(data?.data?.items, getValues().items);
        if (isEqual) {
            toast.info('No changes detected.');
            return;
        }

        const payload = {
            items: getValues().items.map((item) => ({
                quantity: item.quantity,
                unit_price: item.wholesale_price,
                retail_price: item.retail_price,
                product_variation_id: item.product_variation_id,
                product_name: item.product_name,
            })),
        };
        try {
            await updateWholesaleRequest({
                id: data?.data?.id,
                body: payload,
            }).unwrap();
            toast.success('Changes saved successfully!');
        } catch {
            toast.error('Failed to save changes.');
        }
    };

    const buttonStyles = {
        approve:
            'w-full border-green-600 text-green-600 hover:bg-green-600 hover:text-white focus:ring-green-600 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-transparent disabled:text-gray-300 disabled:hover:bg-transparent disabled:hover:text-gray-300',
        save: 'w-full border-blue-400 text-blue-600',
        cancel: 'w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white focus:ring-red-600 disabled:cursor-not-allowed disabled:border-red-300 disabled:bg-transparent disabled:text-red-300 disabled:hover:bg-transparent disabled:hover:text-red-300',
    } as const;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <ConfirmButton
                    confirmText="Are you sure you want to approve this order request?"
                    handleConfirm={() => handleSubmit(getValues())}
                    handleConfirmAndPrint={() =>
                        handleConfirmAndPrint(getValues())
                    }
                >
                    <Button
                        size={'default'}
                        className={buttonStyles.approve}
                        type="button"
                        variant={'outline'}
                        disabled={isConfirming}
                    >
                        {isConfirming ? 'Approving...' : 'Approve'}
                    </Button>
                </ConfirmButton>
                <ConfirmButton
                    confirmText="Are you sure you want to save these changes?"
                    handleConfirm={handleSave}
                >
                    <Button
                        size={'default'}
                        className={buttonStyles.save}
                        type="button"
                        variant={'outline'}
                        disabled={isUpdating}
                    >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </ConfirmButton>
                <ConfirmButton
                    confirmText="Are you sure you want to cancel this order request?"
                    handleConfirm={handleRejectWholesaleRequest}
                >
                    <Button
                        size={'default'}
                        className={buttonStyles.cancel}
                        type="button"
                        variant={'outline'}
                        disabled={isRejecting}
                    >
                        {isRejecting ? 'Cancelling...' : 'Cancel'}
                    </Button>
                </ConfirmButton>
            </CardContent>
        </Card>
    );
};
